import { useState } from 'react';

import type { AutocompleteHighlightChangeReason } from '@mui/material';
import type { KeyboardEvent, SyntheticEvent } from 'react';

import type {
  OrganizationalUnitPerspective,
  PlanningEntityPickerOption
} from '../PlanningEntityPicker.types';

type OrganizationalUnitOption = Extract<
  PlanningEntityPickerOption,
  { kind: 'ORGANIZATIONAL_UNIT' }
>;

type Highlight = { key: string; byKeyboard: boolean };

/**
 * The perspective chosen on the highlighted plan unit row with ←/→, which Enter then picks. Every other row
 * shows its default.
 *
 * The arrows only switch the perspective once the list is navigated with ↑/↓: while typing they move the
 * caret, even with a plan unit row highlighted automatically under the query. Typing again hands them
 * back to the text.
 */
export const useOrganizationalUnitPerspectiveChoice = () => {
  'use memo';

  const [highlight, setHighlight] = useState<Highlight | null>(null);
  const [choice, setChoice] = useState<OrganizationalUnitPerspective | null>(
    null
  );

  /** The perspective the row stands at: the one chosen on it, or its default. */
  const getPerspective = (option: OrganizationalUnitOption) =>
    option.key === highlight?.key &&
    choice &&
    option.perspectives.includes(choice)
      ? choice
      : option.selection.perspective;

  const handleHighlightChange = (
    _event: SyntheticEvent | null,
    option: PlanningEntityPickerOption | null,
    reason: AutocompleteHighlightChangeReason
  ) => {
    // Another row starts at its own default.
    if (option?.key !== highlight?.key) setChoice(null);
    setHighlight(
      option ? { key: option.key, byKeyboard: reason === 'keyboard' } : null
    );
  };

  // A new query starts every row at its default again.
  const handleTyping = () => {
    setChoice(null);
    setHighlight((current) => current && { ...current, byKeyboard: false });
  };

  /**
   * Forget the row and its choice: on a pick and when the list closes. MUI reports no highlight when the
   * list reopens, so without this a plan unit highlighted first in a later search would still stand at
   * the perspective chosen on it before.
   */
  const reset = () => {
    setChoice(null);
    setHighlight(null);
  };

  /** ←/→ on a keyboard-highlighted plan unit row: the perspective beside the current one. True when handled. */
  const handleArrowKey = (
    event: KeyboardEvent<HTMLElement>,
    options: PlanningEntityPickerOption[]
  ) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return false;
    if (!highlight?.byKeyboard) return false;

    const option = options.find(({ key }) => key === highlight.key);
    if (
      option?.kind !== 'ORGANIZATIONAL_UNIT' ||
      option.perspectives.length < 2
    )
      return false;

    const current = option.perspectives.indexOf(getPerspective(option));
    const step = event.key === 'ArrowRight' ? 1 : -1;
    // Stops at either end, as a segmented control does, rather than wrapping round.
    const next = Math.min(
      Math.max(current + step, 0),
      option.perspectives.length - 1
    );
    setChoice(option.perspectives[next]);
    return true;
  };

  return {
    getPerspective,
    handleHighlightChange,
    handleTyping,
    handleArrowKey,
    reset
  };
};
